import BillingRepository from '../repositories/BillingRepository';
import { diContainer, serviceNames } from '../diContainer/diContainer';
import Address, { AddressId } from '../domain/entities/Address';
import { FIELD_TYPE } from '../constants/Constants';
import TransactionLoadHandler, { ITransactionLoad } from './TransactionLoadHandler';
import Config from '../domain/entities/Config';
import NotificationsCollection from '../domain/entities/Notification/NotificationsCollection';
import { PRO } from '../constants/Errors';
import ShippingUIState from '../stores/UIState/ShippingUIState';

class SelectBillingAddressUseCase extends TransactionLoadHandler {
  private id: AddressId;

  private billingRepository: BillingRepository;

  private config: Config;

  private notificationsCollection: NotificationsCollection;

  private shippingUIState: ShippingUIState;

  constructor(id: AddressId, options?: ITransactionLoad) {
    super({ ...options?.transactionLoadOptions, isControlled: true });

    this.id = id;
    this.billingRepository = diContainer.get(serviceNames.billingRepository);
    this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
    this.config = diContainer.get(serviceNames.config);
    this.notificationsCollection = diContainer.get(serviceNames.notificationsCollection);
    this.shippingUIState = diContainer.get(serviceNames.shippingUIState);
  }

  protected async run(): Promise<void> {
    if (this.config.skipBillingAddress) {
      return;
    }

    this.notificationsCollection.clearNotificationByKey(PRO.BILLSHIP_MISMATCH);

    const transaction = await this.transactionRepository.getTransaction();
    const shouldChangeAddress = transaction.billingAddress.id !== this.id || transaction.isMissingBilling || Address.isNewAddress(this.id);
    if (shouldChangeAddress) {
      const { formStore } = this.billingRepository;
      formStore.resetFormValues();
      if (Address.isNewAddress(this.id)) {
        transaction.setBillingAddress(new Address());
        const country = this.config.billingCountries?.find(countryItem => countryItem.default) ||
          this.config.billingCountries?.find(countryItem => countryItem.value === this.config.countryId);
        if (country) {
          formStore.setFieldValue(FIELD_TYPE.COUNTRY, country.label || undefined);
          formStore.setFieldValue(FIELD_TYPE.COUNTRY_ID, country.value);
        }
        if (transaction?.user?.isProCustomer) {
          formStore.setFieldValue(FIELD_TYPE.FIRST_NAME, transaction.user.firstName);
          formStore.setFieldValue(FIELD_TYPE.LAST_NAME, transaction.user.lastName);
        }
      } else {
        // set to first available address if specified address is missing from repository
        const selectedAddress = this.billingRepository.getAddressById(this.id) || this.billingRepository.addresses?.[0];
        if (selectedAddress) {
          try {
            this.shippingUIState.updateContinueLoadingButtonState(true);
            transaction.setBillingAddress(selectedAddress);
            await this.billingRepository.selectAddress(selectedAddress.id);
            await this.handleTransactionUpdate();
          } finally {
            this.shippingUIState.updateContinueLoadingButtonState(false);
          }
        }
      }
    }
  }
}

export default SelectBillingAddressUseCase;
